import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/layout/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Info, Image as ImageIcon, Link as LinkIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const AFFILIATE_ID = 'k9corner02-20';

const CATEGORIES = [
  { value: 'skincare', label: 'Skincare' },
  { value: 'makeup', label: 'Makeup' },
  { value: 'haircare', label: 'Haircare' },
  { value: 'fragrance', label: 'Fragrance' },
  { value: 'bath-body', label: 'Bath & Body' },
  { value: 'tools', label: 'Tools & Brushes' },
];

const AdminAddProduct = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    category: '',
    subcategory: '',
    image_url: '',
    amazon_url: '',
    rating: '',
    review_count: '',
    brand: '',
    is_bestseller: false,
    is_new: false,
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Auto-append affiliate ID to Amazon URL
  const formatAmazonUrl = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('tag', AFFILIATE_ID);
      return urlObj.toString();
    } catch {
      return url;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        rating: parseFloat(formData.rating) || 0,
        review_count: parseInt(formData.review_count) || 0,
        amazon_url: formatAmazonUrl(formData.amazon_url),
      };

      await axios.post(`${API}/api/admin/products`, productData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Product added successfully! It will appear once approved.');
      navigate('/admin/products');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Add New Product">
      <div className="max-w-3xl" data-testid="admin-add-product">
        {/* Info Banner */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800">
              Your Amazon affiliate ID (<code className="bg-blue-100 px-1">{AFFILIATE_ID}</code>) will be automatically added to the product link.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border p-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-heading font-semibold pb-2 border-b">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="mt-1 h-10 rounded-none"
                  placeholder="e.g., CeraVe Hydrating Facial Cleanser"
                  required
                  data-testid="product-name-input"
                />
              </div>

              <div>
                <Label htmlFor="brand">Brand *</Label>
                <Input
                  id="brand"
                  value={formData.brand}
                  onChange={(e) => handleChange('brand', e.target.value)}
                  className="mt-1 h-10 rounded-none"
                  placeholder="e.g., CeraVe"
                  required
                  data-testid="product-brand-input"
                />
              </div>

              <div>
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                  <SelectTrigger className="mt-1 h-10 rounded-none" data-testid="product-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="mt-1 rounded-none min-h-[100px]"
                placeholder="Describe the product..."
                required
                data-testid="product-description-input"
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <h3 className="text-lg font-heading font-semibold pb-2 border-b">Pricing</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">Price ($) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  className="mt-1 h-10 rounded-none"
                  placeholder="29.99"
                  required
                  data-testid="product-price-input"
                />
              </div>

              <div>
                <Label htmlFor="original_price">Original Price ($) <span className="text-neutral-400">(optional)</span></Label>
                <Input
                  id="original_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.original_price}
                  onChange={(e) => handleChange('original_price', e.target.value)}
                  className="mt-1 h-10 rounded-none"
                  placeholder="39.99 (for sale items)"
                  data-testid="product-original-price-input"
                />
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-heading font-semibold pb-2 border-b flex items-center gap-2">
              <LinkIcon className="w-5 h-5" />
              Links
            </h3>
            
            <div>
              <Label htmlFor="amazon_url">Amazon Product URL *</Label>
              <Input
                id="amazon_url"
                type="url"
                value={formData.amazon_url}
                onChange={(e) => handleChange('amazon_url', e.target.value)}
                className="mt-1 h-10 rounded-none"
                placeholder="https://www.amazon.com/dp/XXXXXXXXXX"
                required
                data-testid="product-amazon-url-input"
              />
              <p className="text-xs text-neutral-500 mt-1">
                Paste the Amazon product URL. Your affiliate ID will be added automatically.
              </p>
            </div>

            <div>
              <Label htmlFor="image_url" className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                Image URL *
              </Label>
              <Input
                id="image_url"
                type="url"
                value={formData.image_url}
                onChange={(e) => handleChange('image_url', e.target.value)}
                className="mt-1 h-10 rounded-none"
                placeholder="https://images.unsplash.com/..."
                required
                data-testid="product-image-url-input"
              />
              {formData.image_url && (
                <div className="mt-2">
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="w-24 h-24 object-cover border"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Amazon Data */}
          <div className="space-y-4">
            <h3 className="text-lg font-heading font-semibold pb-2 border-b">Amazon Reviews Data</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="rating">Rating (0-5)</Label>
                <Input
                  id="rating"
                  type="number"
                  step="0.1"
                  min="0"
                  max="5"
                  value={formData.rating}
                  onChange={(e) => handleChange('rating', e.target.value)}
                  className="mt-1 h-10 rounded-none"
                  placeholder="4.5"
                  data-testid="product-rating-input"
                />
              </div>

              <div>
                <Label htmlFor="review_count">Number of Reviews</Label>
                <Input
                  id="review_count"
                  type="number"
                  min="0"
                  value={formData.review_count}
                  onChange={(e) => handleChange('review_count', e.target.value)}
                  className="mt-1 h-10 rounded-none"
                  placeholder="12500"
                  data-testid="product-review-count-input"
                />
              </div>
            </div>
          </div>

          {/* Badges */}
          <div className="space-y-4">
            <h3 className="text-lg font-heading font-semibold pb-2 border-b">Badges</h3>
            
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="flex items-center gap-3">
                <Switch
                  id="is_bestseller"
                  checked={formData.is_bestseller}
                  onCheckedChange={(checked) => handleChange('is_bestseller', checked)}
                  data-testid="product-bestseller-switch"
                />
                <Label htmlFor="is_bestseller">Bestseller</Label>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  id="is_new"
                  checked={formData.is_new}
                  onCheckedChange={(checked) => handleChange('is_new', checked)}
                  data-testid="product-new-switch"
                />
                <Label htmlFor="is_new">New Arrival</Label>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4 pt-4 border-t">
            <Button
              type="submit"
              disabled={loading}
              className="h-12 px-8 rounded-none bg-[#1A1A1A] hover:bg-neutral-800"
              data-testid="submit-product-btn"
            >
              {loading ? <span className="spinner"></span> : 'Add Product'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/products')}
              className="h-12 px-8 rounded-none"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminAddProduct;
